import { BaseApiService } from '../../../services/base/BaseApiService';
import { 
  Conversation, 
  ConversationHistory, 
  ConversationContext,
  Message,
  SendMessageRequest,
  SendMessageResponse,
  Offer,
  ConversationFilters
} from '../types/conversation.types';

// Compatibilidade temporaria com endpoints legados.
// Sucessores: /v1/whatsapp/official, /v1/zapi e /v1/whatsapp/bridge.
const BASE_URL = '/v1/whatsapp';

class ConversationServiceClass extends BaseApiService {
  /**
   * Buscar lista de conversas com filtros
   */
  async getConversations(filters?: ConversationFilters): Promise<Conversation[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.status) params.append('status', filters.status);
      if (filters.cliente) params.append('cliente', filters.cliente);
      if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
      if (filters.dataFim) params.append('dataFim', filters.dataFim);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}/conversas?${queryString}` : `${BASE_URL}/conversas`;
    
    try {
      const response = await this.api.get<{ conversas: Conversation[] }>(url);
      return response.data.conversas || [];
    } catch (error) {
      console.error('Erro ao buscar conversas:', error);
      throw error;
    }
  }

  /**
   * Buscar histórico de mensagens de uma conversa
   */
  async getHistory(clienteId: string, limit: number = 20, offset: number = 0): Promise<ConversationHistory> {
    const url = `${BASE_URL}/historico/${clienteId}?limit=${limit}&offset=${offset}`;
    try {
      const response = await this.api.get<ConversationHistory>(url);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      throw error;
    }
  }

  /**
   * Buscar contexto de uma conversa
   */
  async getContext(clienteId: string): Promise<ConversationContext> {
    const url = `${BASE_URL}/contexto/${clienteId}`;
    try {
      const response = await this.api.get<ConversationContext>(url);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar contexto:', error);
      throw error;
    }
  }

  /**
   * Enviar mensagem
   */
  async sendMessage(clienteId: string, message: SendMessageRequest): Promise<SendMessageResponse> {
    const url = `${BASE_URL}/mensagens/${clienteId}`;
    try {
      const response = await this.api.post<SendMessageResponse>(url, message);
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      throw error;
    }
  }

  /**
   * Upload de áudio
   */
  async uploadAudio(clienteId: string, file: File): Promise<{ audioUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${BASE_URL}/audio/${clienteId}`;
    try {
      const response = await this.api.post<{ audioUrl: string }>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
      throw error;
    }
  }

  /**
   * Upload de anexo
   */
  async uploadAttachment(clienteId: string, file: File): Promise<{ attachmentUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${BASE_URL}/anexos/${clienteId}`;
    try {
      const response = await this.api.post<{ attachmentUrl: string }>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar anexo:', error);
      throw error;
    }
  }

  /**
   * Registrar resposta do cliente
   */
  async registerResponse(clienteId: string, response: any): Promise<any> {
    const url = `${BASE_URL}/respostas/${clienteId}`;
    try {
      const result = await this.api.post<any>(url, response);
      return result.data;
    } catch (error) {
      console.error('Erro ao registrar resposta:', error);
      throw error;
    }
  }

  /**
   * Calcular oferta
   */
  async calculateOffer(clienteId: string, linhasIds: string[]): Promise<Offer> {
    const url = `${BASE_URL}/ofertas/calcular`;
    try {
      const response = await this.api.post<Offer>(url, { clienteId, linhasIds });
      return response.data;
    } catch (error) {
      console.error('Erro ao calcular oferta:', error);
      throw error;
    }
  }

  /**
   * Marcar linhas como desativadas
   */
  async markLinesDeactivated(clienteId: string, linhasIds: string[], motivo: string): Promise<any> {
    const url = `${BASE_URL}/linhas/${clienteId}/desativar`;
    try {
      const response = await this.api.put<any>(url, { linhasIds, motivo });
      return response.data;
    } catch (error) {
      console.error('Erro ao marcar linhas como desativadas:', error);
      throw error;
    }
  }

  /**
   * Escalar para atendimento humano
   */
  async escalateToHuman(clienteId: string, motivo: string, ultimaMensagem?: string): Promise<any> {
    const url = `${BASE_URL}/cliente/${clienteId}/marcar-atendente`;
    try {
      const response = await this.api.post<any>(url, { motivo, ultimaMensagem });
      return response.data;
    } catch (error) {
      console.error('Erro ao escalar para atendimento:', error);
      throw error;
    }
  }
}

export const ConversationService = new ConversationServiceClass();
